// The Projects home dashboard (PE15, rev. 1.6): the health of the business across projects. Revenue
// for the next 12 months, booked and weighted pipeline; margin per project against its target; the
// pipeline by status; and everyone's load for the next 8 weeks. Money needs financials.read, and the
// API sends none without it.

import { useState } from "react";
import { Body1, Button, Caption1, Card, Subtitle2, Tab, TabList } from "@fluentui/react-components";
import { STATUS_LABEL, statusOf } from "../../../core";
import { useApi, type ProjectsDashboardResponse } from "../api";
import { count, day, money, pct } from "../format";
import { href } from "../router";
import { ErrorBar, Kpi, Loading, useMe } from "../ui";
import { MarginBars, RevenueChart, RevenueLegend, RevenueTable } from "./Charts";

const monthName = (m: string) => m.slice(0, 7);

export function ProjectsDashboard() {
  // Past months with reported hours carry their actual cost by default (PP9, rev. 1.16).
  const [costView, setCostView] = useState<"actual" | "calculated">("actual");
  const data = useApi<ProjectsDashboardResponse>(`/projects/dashboard?cost=${costView}`);
  const [table, setTable] = useState(false);
  const [full, setFull] = useState(false);
  const [month, setMonth] = useState<string | null>(null);
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const m = d.money;
  const span = `${monthName(d.window.from)} – ${monthName(d.window.to)}`;
  // A month picked on Revenue by month narrows the margins to that month (rev. 1.16).
  const marginRows = !m
    ? []
    : !month
      ? m.projects
      : m.projects
          .flatMap((p) => {
            const x = p.months[month];
            return x && x.revenue > 0 ? [{ ...p, revenue: x.revenue, cost: x.cost, contributionPct: (x.revenue - x.cost) / x.revenue }] : [];
          })
          .sort((a, b) => a.contributionPct - b.contributionPct);
  const marginOk = m?.kpis.bookedMargin12 != null && m.kpis.bookedMargin12 >= d.targetMargin - 1e-9;

  return (
    <div className="stack">
      {m && (
        <div className="kpis" aria-label="Business health">
          <Kpi label="Booked revenue, next 12 months" value={money(m.kpis.booked12)} sub={span} />
          <Kpi label="Weighted pipeline, next 12 months" value={money(m.kpis.pipeline12)} sub="Prospect, opportunity and pending, × probability" />
          <Kpi
            label="Booked margin, next 12 months"
            value={m.kpis.bookedMargin12 === null ? "—" : pct(m.kpis.bookedMargin12)}
            tone={m.kpis.bookedMargin12 === null ? undefined : marginOk ? "good" : "bad"}
            sub={`Against the ${pct(d.targetMargin, 0)} target, at Base rates`}
          />
          <Kpi label="Backlog" value={money(m.kpis.backlog)} sub="Booked revenue from this month on" />
          <Kpi
            label="Projects below target"
            value={`${count(m.kpis.belowTarget)} of ${count(m.kpis.measured)}`}
            tone={m.kpis.measured ? (m.kpis.belowTarget ? "bad" : "good") : undefined}
            flag={m.kpis.belowTarget ? "Need attention" : "All on target"}
            sub="Booked and pending projects"
          />
        </div>
      )}

      {m && (
        <Card className="panel wide">
          <div className="panel-head">
            <Subtitle2>Revenue by month, {span}</Subtitle2>
            <div className="toolbar">
              <TabList size="small" selectedValue={full ? "full" : "weighted"} onTabSelect={(_, x) => setFull(x.value === "full")} aria-label="Pipeline shown">
                <Tab value="weighted">Pipeline weighted</Tab>
                <Tab value="full">Full value</Tab>
              </TabList>
              <Button size="small" appearance="subtle" onClick={() => setTable(!table)}>
                {table ? "Show chart" : "Show as a table"}
              </Button>
            </div>
          </div>
          {table ? (
            <RevenueTable months={m.months} />
          ) : (
            <>
              <RevenueLegend full={full} />
              <RevenueChart months={m.months} full={full} selected={month} onSelect={setMonth} />
            </>
          )}
          <Caption1 className="muted">
            Recognized revenue. {full ? "Pipeline is shown at each project's full revenue." : "Pipeline is each project's revenue × its probability."} Booked is counted in full.
            Click a month to filter Margin by project to it.
          </Caption1>
        </Card>
      )}

      <div className="card-grid">
        {m && (
          <Card className="panel">
            <div className="panel-head">
              <Subtitle2>Margin by project{month ? `, ${month}` : ""}, lowest first</Subtitle2>
              <div className="toolbar">
                {month && (
                  <Button size="small" appearance="subtle" onClick={() => setMonth(null)}>
                    Whole plan
                  </Button>
                )}
                <TabList size="small" selectedValue={costView} onTabSelect={(_, x) => setCostView(x.value as "actual" | "calculated")} aria-label="Cost of reported hours">
                  <Tab value="actual">Actual cost</Tab>
                  <Tab value="calculated">Calculated</Tab>
                </TabList>
              </div>
            </div>
            <Caption1 className="muted">Booked and pending projects, {month ? `in ${month} only` : "over their whole plan"}. The tick is each project's target.{" "}
              {month ? "" : `Months already reported are at ${costView === "actual" ? "actual cost: what each person cost that month, over the hours they reported" : "calculated cost of the hours reported"}; the rest is the plan.`}</Caption1>
            {marginRows.length ? (
              <MarginBars projects={marginRows} link={(id) => href("projects", id)} />
            ) : (
              <Body1 className="muted">{month ? `No booked or pending project has revenue in ${month}.` : "No booked or pending project has revenue yet."}</Body1>
            )}
          </Card>
        )}
        <Card className="panel">
          <Subtitle2>Pipeline by status</Subtitle2>
          <table className="month-table" aria-label="Projects by status">
            <thead>
              <tr>
                <th>Status</th>
                <th className="num">Projects</th>
                {m && <th className="num">Value</th>}
                {m && <th className="num">Weighted</th>}
              </tr>
            </thead>
            <tbody>
              {d.pipeline.map((p) => (
                <tr key={p.status}>
                  <td>{STATUS_LABEL[p.status]}</td>
                  <td className="num">{count(p.count)}</td>
                  {m && <td className="num">{money(p.value ?? 0)}</td>}
                  {m && <td className="num">{money(p.weighted ?? 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          <Caption1 className="muted">
            Closed: {count(d.counts.delivered ?? 0)} delivered, {count(d.counts.lost ?? 0)} lost, {count(d.counts.cancelled ?? 0)} cancelled.
          </Caption1>
        </Card>
      </div>

      <LoadTable load={d.load} />
    </div>
  );
}

/** Everyone's load for the next 8 weeks, colored by §3.9 with the percentage written in each cell. */
function LoadTable({ load }: { load: ProjectsDashboardResponse["load"] }) {
  const me = useMe();
  const people = me.can("comp.read");
  const { over, under } = load.thresholds;
  const cell = (v: number | null) => {
    if (v === 0) return { text: "", cls: "" };
    if (v === null) return { text: "no cap.", cls: "load-over" };
    return { text: v > 9.99 ? ">999%" : pct(v, 0), cls: `load-${statusOf(v, load.thresholds)}` };
  };
  return (
    <Card className="panel wide">
      <Subtitle2>Load, the next 8 weeks</Subtitle2>
      <Caption1 className="muted">
        Planned hours on {load.counted.join(" and ")} projects ÷ capacity. Red over {pct(over, 0)}, teal on target, blue under {pct(under, 0)}; busiest first.
      </Caption1>
      {load.people.length === 0 ? (
        <Body1 className="muted">No one is active.</Body1>
      ) : (
        <div className="table-scroll">
          <table className="load-table" aria-label="Load by person and week">
            <thead>
              <tr>
                <th>Person</th>
                {load.weekStarts.map((w) => (
                  <th key={w} className="num">
                    {day(w).replace(/, \d{4}$/, "")}
                  </th>
                ))}
                <th className="num">Average</th>
              </tr>
            </thead>
            <tbody>
              {load.people.map((p) => (
                <tr key={p.personId}>
                  <th scope="row">{people ? <a href={href("people", p.personId)}>{p.name}</a> : p.name}</th>
                  {p.weeks.map((v, i) => {
                    const c = cell(v);
                    return (
                      <td key={i} className={`num ${c.cls}`}>
                        {c.text}
                      </td>
                    );
                  })}
                  <td className="num">{p.average === null ? "—" : pct(p.average, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
